import { biDisplayFormat } from '@euler/model/bi/format';
import {
  BiFieldRole,
  BiFieldType,
  BiResultColumn,
} from '@euler/model/bi/result';
import {
  BiFormat,
  BiFunnelShow,
  BiFunnelViz,
  BiPalette,
  BiVizType,
} from '@euler/model/bi/viz';
import {
  BiGalleryCategory,
  BiGalleryCategoryKey,
  BiGalleryDashboardCard,
  BiGalleryVariant,
} from '../functions/biGalleryTypes';
import { galleryReady, galleryResult } from './biGalleryFields';

function count(key: string, zh: string, unit: string): BiResultColumn {
  return {
    key,
    label: { zh, en: key },
    type: BiFieldType.Number,
    role: BiFieldRole.Measure,
    format: { ...biDisplayFormat(BiFormat.Integer), unit },
  };
}

function stagesCard(
  title: string,
  columns: BiResultColumn[],
  values: number[],
): BiGalleryDashboardCard {
  return {
    surface: 'dashboard',
    title,
    viz: {
      type: BiVizType.Funnel,
      series: columns.map(column => ({ key: column.key })),
    },
    data: galleryReady(galleryResult(columns, [values])),
  };
}

const sales = stagesCard(
  '近 12 个月线索转化漏斗',
  [
    count('lead_count', '线索数', '条'),
    count('booked_count', '已预约线索数', '条'),
    count('arrived_count', '已到店线索数', '条'),
    count('ordered_count', '已开单线索数', '条'),
    count('settled_count', '已结算线索数', '条'),
  ],
  [1240, 486, 392, 301, 276],
);

const repair = stagesCard(
  '本年维修建议转化漏斗',
  [
    count('recommended_count', '建议项数', '项'),
    count('quoted_count', '已报价建议项数', '项'),
    count('converted_count', '已转施工建议项数', '项'),
    count('settled_count', '已结算建议项数', '项'),
  ],
  [3820, 1640, 1112, 1048],
);

const engagement = stagesCard(
  '本月报告触达',
  [
    count('order_count', '工单数', '单'),
    count('shared_count', '已分享报告工单数', '单'),
    count('viewed_count', '客户已查看工单数', '单'),
  ],
  [2184, 1560, 1206],
);

const status = {
  key: 'status',
  label: { zh: '状态', en: 'status' },
  type: BiFieldType.String,
  role: BiFieldRole.Dimension,
};
const leads = count('lead_count', '线索数', '条');

const byStatus: BiGalleryDashboardCard = {
  surface: 'dashboard',
  title: '线索按状态分步',
  viz: {
    type: BiVizType.Funnel,
    series: [{ key: leads.key }],
    stagesBy: {
      key: status.key,
      order: ['新建', '跟进中', '已预约', '已成交'],
    },
  },
  data: galleryReady(
    galleryResult(
      [status, leads],
      // Any order, and a value the stage order leaves out (not drawn).
      [
        ['已成交', 64],
        ['已流失', 210],
        ['新建', 520],
        ['已预约', 148],
        ['跟进中', 336],
      ],
    ),
  ),
};

const empty = stagesCard(
  '本周线索转化漏斗',
  [
    count('lead_count', '线索数', '条'),
    count('booked_count', '已预约线索数', '条'),
    count('arrived_count', '已到店线索数', '条'),
    count('ordered_count', '已开单线索数', '条'),
  ],
  [18, 4, 0, 0],
);

function withStyle(
  key: string,
  name: string,
  note: string,
  card: BiGalleryDashboardCard,
  style: NonNullable<BiFunnelViz['style']>,
): BiGalleryVariant {
  return { key, name, note, card: { ...card, viz: { ...card.viz, style } } };
}

export const kFunnelCategory: BiGalleryCategory = {
  key: BiGalleryCategoryKey.Funnel,
  name: '漏斗图',
  description: '销售、维修、报告三个转化漏斗，按维度分步、标签与配色',
  preview: sales,
  variants: [
    {
      key: 'sales',
      name: '线索到结算',
      note: '五个指标按步骤顺序：数值、较上一步、占首步',
      card: sales,
    },
    {
      key: 'repair',
      name: '建议到结算',
      note: '维修转化漏斗',
      card: repair,
    },
    {
      key: 'engagement',
      name: '工单到客户查看',
      note: '三步',
      card: engagement,
    },
    {
      key: 'stages-by',
      name: '按维度取值分步',
      note: 'stagesBy = 状态，按给定顺序；不在顺序里的取值不画',
      card: byStatus,
    },
    withStyle('rates', '只显示转化率', 'funnel.show = rates', sales, {
      funnel: { show: BiFunnelShow.Rates },
    }),
    withStyle('values', '只显示数值', 'funnel.show = values', repair, {
      funnel: { show: BiFunnelShow.Values },
    }),
    withStyle('warm', '暖色配色', 'palette = warm', engagement, {
      palette: BiPalette.Warm,
    }),
    withStyle('no-labels', '不显示标签', 'dataLabels = false', sales, {
      dataLabels: false,
    }),
    {
      key: 'zero',
      name: '后几步为 0',
      note: '0 之后没有转化率',
      card: empty,
    },
  ],
};
